import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Safely extract a human-readable message from ANY axios error.
 *
 * FastAPI returns errors in a few shapes and if we blindly render whichever
 * shape happens to arrive we crash React with:
 *
 *   Objects are not valid as a React child (found: object with keys
 *   {type, loc, msg, input, url})
 *
 * That is a Pydantic 422 validation payload — `detail` is an ARRAY of
 * error objects, not a string. This helper normalises every shape:
 *
 *   { detail: "not found" }              → "not found"
 *   { detail: [ { msg: "field required", loc: [...] }, ... ] }
 *                                        → "field required · field required · ..."
 *   { message: "Something broke" }       → "Something broke"
 *   Network error / no response          → fallback
 *
 * Use in place of `e?.response?.data?.detail || "…"`:
 *
 *   catch (e) { toast.error(getApiError(e, "Bulk status change failed")); }
 */
export function getApiError(err, fallback = "Something went wrong. Please try again.") {
  const data = err?.response?.data;

  if (data == null) {
    return err?.message || fallback;
  }

  // 1. Plain string body (rare, but some middlewares do this)
  if (typeof data === "string") return data;

  // 2. FastAPI HTTPException — always { detail: <string> }
  if (typeof data.detail === "string") return data.detail;

  // 3. Pydantic 422 — { detail: [{ msg, loc, type, ... }, ...] }
  // Include the field path (last segment of `loc`) so the user knows
  // WHICH field is invalid — e.g. `facility_ids.7: Input should be a valid string`.
  if (Array.isArray(data.detail)) {
    const msgs = data.detail
      .map((d) => {
        if (typeof d === "string") return d;
        const msg = d?.msg || d?.message;
        if (!msg) return null;
        const loc = Array.isArray(d?.loc) ? d.loc.slice(1) : []; // drop leading "body"
        const field = loc.length ? loc.join(".") : null;
        return field ? `${field}: ${msg}` : msg;
      })
      .filter(Boolean);
    if (msgs.length) return msgs.join(" · ");
  }

  // 4. Other common shapes some libraries emit
  if (typeof data.message === "string") return data.message;
  if (typeof data.error === "string") return data.error;

  // 5. Give up but don't crash — never return a raw object
  return fallback;
}
